import { useApi } from '@/api';
import {
  CurrencyTooltipChange,
  EntityName,
  KpiSkeleton,
  MultipleKpiCard
} from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  Box,
  CryptoIcon,
  Flex,
  FlexProps,
  PercentageValue,
  useIsMobile
} from '@blockanalitica/ui';
import styled, { css } from 'styled-components';

const KpiAPY = styled(Flex)<FlexProps>`
  ${(props) => css`
    font-size: ${props.theme.sizes['1']};
    color: ${props.theme.colors.success};
  `}
`;

export default function LSESKYFarmsKpi({ daysAgo }: { daysAgo: string }) {
  const estimatedAbbrev = useIsMobile() ? 'Est.' : 'Estimated';
  const [data, error] = useApi({
    url: `/farms/staked/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  const isMobile = useIsMobile();

  if (error) {
    throw error;
  }

  if (data && Array.isArray(data)) {
    return (
      <>
        {data
          .filter((farm) => farm.tokens_per_second > 0)
          .map((farm) => {
            const farmFooter = [
              {
                title: 'Total Staked',
                value: (
                  <CurrencyTooltipChange
                    field="total_staked"
                    data={farm}
                    options={{
                      currency: tokensMapping('SKY'),
                      iconSize: '0'
                    }}
                  />
                )
              },
              {
                title: `Total ${estimatedAbbrev} ${farm.rewards_token_symbol} Rewards`,
                value: (
                  <CurrencyTooltipChange
                    field="total_farmed"
                    data={farm}
                    options={{
                      currency: {
                        name: farm.rewards_token_symbol,
                        address: farm.rewards_token_address
                      },
                      iconSize: '0'
                    }}
                  />
                )
              }
            ];

            if (!isMobile) {
              farmFooter.push({
                title: 'Stakers',
                value: <CurrencyTooltipChange field="depositors" data={farm} />
              });
            }

            return (
              <MultipleKpiCard
                key={farm.address}
                linkTo={farm.address}
                title={
                  <Flex justifyContent="space-between" flex="1">
                    <Flex gap="s" alignItems="flex-end">
                      <EntityName
                        symbol1={
                          <CryptoIcon
                            name={tokensMapping('SKY').name}
                            address={tokensMapping('SKY').address}
                            size="2"
                          />
                        }
                        symbol2={
                          <CryptoIcon
                            name={tokensMapping(farm.rewards_token_symbol).name}
                            address={
                              tokensMapping(farm.rewards_token_symbol).address
                            }
                            size="2"
                          />
                        }
                        fontSize="1"
                        color="text">
                        Stake {tokensMapping('SKY').name} to get{' '}
                        {tokensMapping(farm.rewards_token_symbol).name} rewards
                      </EntityName>
                    </Flex>
                    <KpiAPY alignItems="center" gap="3xs">
                      <Box color="muted">APY:</Box>
                      <PercentageValue
                        color="white"
                        value={farm.apy}
                        options={{ tooltip: false }}
                      />
                    </KpiAPY>
                  </Flex>
                }
                footer={farmFooter}
              />
            );
          })}
      </>
    );
  }

  return <KpiSkeleton />;
}
